import React, { useState } from 'react';
import { Plus, Pencil, Trash2, ClipboardList, Search, Download, Mail, Power, Users, Link, BarChart2 } from 'lucide-react';
import { Button, Modal, EmptyState, Badge, Toggle, RespondersModal } from '../components/common';
import { downloadInstanceReports, getRespondersByInstance } from '../services/api';
import { copyRegisterLink } from '../utils/clipboard';
import type {
    Survey,
    Organization,
    SurveyTemplate,
    CreateSurveyPayload,
    UpdateSurveyPayload,
} from '../types';

interface InstancesPageProps {
    instances: Survey[];
    organizations: Organization[];
    templates: SurveyTemplate[];
    loading: boolean;
    onCreate: (payload: CreateSurveyPayload) => Promise<void>;
    onUpdate: (payload: UpdateSurveyPayload) => Promise<void>;
    onDelete: (instanceId: string) => Promise<void>;
    onShowToast: (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => void;
    onNavigateToAnalytics?: (instanceId: string) => void;
    onNavigateToRespondent?: (respondentId: string, context: { instanceId?: string; templateId?: number }) => void;
}

export const InstancesPage: React.FC<InstancesPageProps> = ({
    instances,
    organizations,
    templates,
    loading,
    onCreate,
    onUpdate,
    onDelete,
    onShowToast,
    onNavigateToAnalytics,
    onNavigateToRespondent,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingInstance, setEditingInstance] = useState<Survey | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
    const [formData, setFormData] = useState({
        name: '',
        org_id: '',
        template_id: '',
        is_active: false,
        send_report_to_email: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [isRespondersModalOpen, setIsRespondersModalOpen] = useState(false);
    const [viewingRespondersInstance, setViewingRespondersInstance] = useState<Survey | null>(null);

    const filteredInstances = instances.filter((instance: Survey) => {
        const matchesSearch = instance.name?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesFilter =
            filterActive === 'all' ||
            (filterActive === 'active' && instance.is_active) ||
            (filterActive === 'inactive' && !instance.is_active);
        return matchesSearch && matchesFilter;
    });

    const getOrgName = (orgId: string) => {
        const org = organizations.find((o: Organization) => o.id === orgId);
        return org?.name || 'לא ידוע';
    };

    const getTemplateName = (templateId: number) => {
        // Robust ID lookup using loose equality for strings vs numbers from DB
        const template = templates.find((t: SurveyTemplate) => t.id == templateId);
        return template?.name || 'לא ידוע';
    };

    const openCreateModal = () => {
        setEditingInstance(null);
        setFormData({
            name: '',
            org_id: organizations[0]?.id || '',
            template_id: templates[0]?.id?.toString() || '',
            is_active: false,
            send_report_to_email: false,
        });
        setIsModalOpen(true);
    };

    const openEditModal = (instance: Survey) => {
        setEditingInstance(instance);
        setFormData({
            name: instance.name,
            org_id: instance.organization_id,
            template_id: instance.template_id.toString(),
            is_active: instance.is_active,
            send_report_to_email: instance.send_report_to_email,
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingInstance) {
                await onUpdate({
                    instance_id: editingInstance.id,
                    name: formData.name,
                    is_active: formData.is_active,
                    send_report_to_email: formData.send_report_to_email,
                });
            } else {
                await onCreate({
                    name: formData.name,
                    org_id: formData.org_id,
                    template_id: parseInt(formData.template_id, 10),
                    is_active: formData.is_active,
                    send_report_to_email: formData.send_report_to_email,
                });
            }
            setIsModalOpen(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (instance: Survey) => {
        if (window.confirm(`האם למחוק את הסקר "${instance.name}"? פעולה זו אינה הפיכה.`)) {
            await onDelete(instance.id);
        }
    };

    const handleToggleActive = async (instance: Survey) => {
        await onUpdate({
            instance_id: instance.id,
            name: instance.name,
            is_active: !instance.is_active,
            send_report_to_email: instance.send_report_to_email,
        });
    };

    const handleDownloadReports = (instanceId: string) => {
        // Immediate feedback
        onShowToast('info', 'מוריד דוחות', 'תהליך הפקת הדוחות החל ברקע...');

        // Background process
        downloadInstanceReports(instanceId).then(() => {
            onShowToast('success', 'הורדה הושלמה', 'הדוחות ירדו בהצלחה');
        }).catch(error => {
            onShowToast('error', 'שגיאה בהורדת דוחות', 'אירעה שגיאה בעת ניסיון להפיק את הדוחות');
            console.error('Error downloading reports:', error);
        });
    };

    const handleCopyRegisterLink = (instanceId: string) => copyRegisterLink(instanceId, onShowToast);

    return (
        <div>
            {/* Header Actions */}
            <div className="page-header-actions">
                <div className="page-header-filters">
                    <div className="search-wrapper">
                        <Search
                            size={18}
                            className="search-icon"
                        />
                        <input
                            type="text"
                            placeholder="חיפוש סקרים..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="filter-tabs">
                        {(['all', 'active', 'inactive'] as const).map((filter) => (
                            <button
                                key={filter}
                                className={`btn btn-sm ${filterActive === filter ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setFilterActive(filter)}
                            >
                                {filter === 'all' ? 'הכל' : filter === 'active' ? 'פעילים' : 'לא פעילים'}
                            </button>
                        ))}
                    </div>
                </div>
                <Button icon={<Plus size={18} />} onClick={openCreateModal}>
                    קבוצת סקר חדשה
                </Button>
            </div>

            {/* Instances Table */}
            <div className="card">
                {loading ? (
                    <div className="loading-overlay">
                        <div className="spinner" />
                        <span className="loading-text">טוען סקרים...</span>
                    </div>
                ) : instances.length === 0 ? (
                    <EmptyState
                        icon={<ClipboardList size={64} />}
                        title="אין סקרים עדיין"
                        description="צור קבוצת סקר חדשה על ידי בחירת ארגון ותבנית"
                        action={
                            <Button icon={<Plus size={18} />} onClick={openCreateModal}>
                                קבוצת סקר חדשה
                            </Button>
                        }
                    />
                ) : (
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>שם הסקר</th>
                                    <th>ארגון</th>
                                    <th>תבנית</th>
                                    <th>סטטוס</th>
                                    <th>שליחת דוח</th>
                                    <th>תאריך יצירה</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredInstances.map((instance: Survey) => (
                                    <tr key={instance.id}>
                                        <td data-label="שם הסקר">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                                <div
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 'var(--radius-lg)',
                                                        background: instance.is_active
                                                            ? 'linear-gradient(135deg, var(--success-100), var(--success-200))'
                                                            : 'linear-gradient(135deg, var(--neutral-100), var(--neutral-200))',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: instance.is_active ? 'var(--success-600)' : 'var(--neutral-500)',
                                                    }}
                                                >
                                                    <ClipboardList size={18} />
                                                </div>
                                                <span style={{ fontWeight: 500 }}>{instance.name}</span>
                                            </div>
                                        </td>
                                        <td data-label="ארגון">{getOrgName(instance.organization_id)}</td>
                                        <td data-label="תבנית">{getTemplateName(instance.template_id)}</td>
                                        <td data-label="סטטוס">
                                            <Badge variant={instance.is_active ? 'success' : 'neutral'}>
                                                {instance.is_active ? 'פעיל' : 'לא פעיל'}
                                            </Badge>
                                        </td>
                                        <td data-label="שליחת דוח">
                                            {instance.send_report_to_email ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', color: 'var(--success-600)' }}>
                                                    <Mail size={16} />
                                                    <span style={{ fontSize: 'var(--font-size-xs)' }}>כן</span>
                                                </div>
                                            ) : (
                                                <span style={{ color: 'var(--neutral-400)', fontSize: 'var(--font-size-xs)' }}>לא</span>
                                            )}
                                        </td>
                                        <td data-label="תאריך יצירה" style={{ color: 'var(--neutral-500)' }}>
                                            {new Date(instance.created_at).toLocaleDateString('he-IL')}
                                        </td>
                                        <td data-label="פעולות">
                                            <div className="cell-actions">
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleCopyRegisterLink(instance.id)}
                                                    title="העתק קישור להרשמה"
                                                >
                                                    <Link size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleToggleActive(instance)}
                                                    title={instance.is_active ? 'כיבוי' : 'הפעלה'}
                                                    style={{ color: instance.is_active ? 'var(--success-500)' : 'var(--warning-500)' }}
                                                >
                                                    <Power size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => {
                                                        setViewingRespondersInstance(instance);
                                                        setIsRespondersModalOpen(true);
                                                    }}
                                                    title="צפה במשיבים"
                                                >
                                                    <Users size={16} />
                                                </button>
                                                {!organizations.find(o => o.id === instance.organization_id)?.is_global && (
                                                    <button
                                                        className="btn btn-icon btn-ghost sm"
                                                        onClick={() => handleDownloadReports(instance.id)}
                                                        title="הורד דוחות"
                                                    >
                                                        <Download size={16} />
                                                    </button>
                                                )}
                                                {onNavigateToAnalytics && (
                                                    <button
                                                        className="btn btn-icon btn-ghost sm"
                                                        onClick={() => onNavigateToAnalytics(instance.id)}
                                                        title="ניתוח תוצאות"
                                                        style={{ color: 'var(--primary-400)' }}
                                                    >
                                                        <BarChart2 size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => openEditModal(instance)}
                                                    title="עריכה"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleDelete(instance)}
                                                    title="מחיקה"
                                                    style={{ color: 'var(--error-500)' }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingInstance ? 'עריכת סקר' : 'קבוצת סקר חדשה'}
                footer={
                    <>
                        <Button onClick={handleSubmit} loading={submitting}>
                            {editingInstance ? 'שמור שינויים' : 'צור סקר'}
                        </Button>
                        <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                            ביטול
                        </Button>
                    </>
                }
            >
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>שם הסקר *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="לדוגמה: סקר רבעון 1 2024"
                            required
                        />
                    </div>

                    {!editingInstance && (
                        <>
                            <div className="form-group">
                                <label>ארגון *</label>
                                <select
                                    value={formData.org_id}
                                    onChange={(e) => setFormData({ ...formData, org_id: e.target.value })}
                                    required
                                >
                                    <option value="">בחר ארגון</option>
                                    {organizations.map((org: Organization) => (
                                        <option key={org.id} value={org.id}>
                                            {org.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>תבנית סקר *</label>
                                <select
                                    value={formData.template_id}
                                    onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                                    required
                                >
                                    <option value="">בחר תבנית</option>
                                    {templates.map((template: SurveyTemplate) => (
                                        <option key={template.id} value={template.id}>
                                            {template.name || `תבנית #${template.id}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <Toggle
                            checked={formData.is_active}
                            onChange={(checked: boolean) => setFormData({ ...formData, is_active: checked })}
                            label="סקר פעיל"
                        />
                        <span className="form-help">כאשר הסקר פעיל, משתמשים יוכלו למלא אותו</span>
                    </div>

                    <div className="form-group">
                        <Toggle
                            checked={formData.send_report_to_email}
                            onChange={(checked: boolean) => setFormData({ ...formData, send_report_to_email: checked })}
                            label="שליחת דוח לאימייל"
                        />
                        <span className="form-help">שלח את הדוח האישי לאימייל המשתמש לאחר מילוי הסקר</span>
                    </div>
                </form>
            </Modal>

            <RespondersModal
                isOpen={isRespondersModalOpen}
                onClose={() => setIsRespondersModalOpen(false)}
                title={`משיבים בסקר: ${viewingRespondersInstance?.name}`}
                fetchResponders={() => getRespondersByInstance(viewingRespondersInstance?.id || '')}
                onNavigateToAnalytics={(respondentId) => onNavigateToRespondent?.(respondentId, { instanceId: viewingRespondersInstance?.id })}
            />
        </div>
    );
};
